import React, {
  useCallback, useEffect, useId, useMemo, useRef, useState,
} from 'react';
import { Link } from 'react-router-dom';
import {
  Activity, AlertTriangle, Anchor, CheckCircle2, ChevronDown, ChevronLeft,
  ChevronRight,
  CircleSlash, Coins, Edit3, Gamepad2, Gift, Info, Link2, ListChecks,
  Loader2, Percent, Plus, Power, RefreshCw, Save, Search, Send, Settings2,
  ShieldCheck, Sparkles, Ticket, Timer, Trash2, Unlink, Users, X, Zap,
} from 'lucide-react';
import client from '../lib/api';
import { useToast } from '../hooks/use-toast';
import { useAdminPermissions } from './lib/permissions';
import { AdminEmpty, AdminError } from './components/PageState';
import ConfirmDialog from './components/ConfirmDialog';
// Sayfa boyutu seçenekleri panelin bütün liste ekranlarıyla ortaktır.
import { PAGE_SIZE_OPTIONS, pageWindow, readPageSize, writePageSize }
  from './lib/pagination';
import { formatMoney, formatNumber } from './lib/formatters';
import './anchor.css';

const PALETTE = [
  '#38bdf8', '#a78bfa', '#fbbf24', '#34d399', '#f472b6',
  '#ef4444', '#60a5fa', '#facc15',
];

const AUTO_VIDEO_VALUE = 'oto';

const emptyPrize = {
  value: 20,
  weight: 10,
  color: '#38bdf8',
  active: false,
  order: 0,
};

const STATUS_LABELS = {
  pending: 'Tür seçimi bekleniyor',
  completed: 'Tamamlandı',
  awaiting_provider: 'Sağlayıcı kuyruğunda',
  // Üye kendi freespin hakkından vazgeçebiliyor; etiket eksikken panelde ham
  // `cancelled` yazıyordu.
  cancelled: 'Üye iptal etti',
};

const REWARD_LABELS = {
  '': 'Seçilmedi',
  freespin: 'Freespin',
  freebet: 'Freebet',
};

const FREEBET_STATUS_LABELS = {
  active: 'Kullanılabilir',
  used: 'Kullanıldı',
  expired: 'Süresi doldu',
};

/** Atış listesinin durum süzgeçleri.
 *
 * Değerler backend'in `status` parametresine olduğu gibi gider; boş dize
 * "hepsi" demektir. Liste bir süre süzgeçsizdi ve uç en çok 100 satır
 * döndürdüğü için yüzüncü atıştan sonrası hiçbir yerden görünmüyordu
 * (KYC listesindeki arızanın aynısı).
 */
export const THROW_STATUS_FILTERS = [
  { id: '', label: 'Tümü' },
  { id: 'awaiting_provider', label: 'Kuyrukta' },
  { id: 'completed', label: 'Tamamlandı' },
  { id: 'pending', label: 'Tür bekleniyor' },
  { id: 'cancelled', label: 'İptal' },
];

export const FREEBET_STATUS_FILTERS = [
  { id: '', label: 'Tümü' },
  { id: 'active', label: 'Kullanılabilir' },
  { id: 'used', label: 'Kullanıldı' },
  { id: 'expired', label: 'Süresi doldu' },
];


// Formun kendi Kaydet düğmesiyle yazılan alanlar. `anchor_enabled` ve
// `anchor_freespin_auto_delivery` bilinçli olarak dışarıdadır: ikisi de tek
// tıkla kaydedilen anahtarlardır (bkz. `saveSettings`).
const SETTINGS_FORM_KEYS = [
  'anchor_title', 'anchor_description', 'anchor_video_url',
  'anchor_cooldown_hours', 'anchor_min_deposit',
  'anchor_require_telegram', 'anchor_single_ip',
  'anchor_freebet_min_odd', 'anchor_freebet_expiry_days',
];

const NUMERIC_SETTING_KEYS = new Set([
  'anchor_cooldown_hours', 'anchor_min_deposit',
  'anchor_freebet_min_odd', 'anchor_freebet_expiry_days',
]);

const BOOLEAN_SETTING_KEYS = new Set([
  'anchor_require_telegram', 'anchor_single_ip',
]);

const formatDate = (value) => (
  value ? new Date(value).toLocaleString('tr-TR') : '-');

const splitDate = (value) => {
  if (!value) return { day: '-', time: '' };
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return { day: '-', time: '' };
  return {
    day: date.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: '2-digit' }),
    time: date.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }),
  };
};

const initials = (value) => String(value || '?')
  .trim()
  .slice(0, 2)
  .toLocaleUpperCase('tr-TR');

const normalizeSearch = (value) => String(value || '')
  .trim()
  .toLocaleLowerCase('tr-TR');

export const filterCampaignVendors = (items, query) => {
  const needle = normalizeSearch(query);
  return [...items]
    .filter((vendor) => !needle || normalizeSearch(vendor).includes(needle))
    .sort((left, right) => String(left).localeCompare(String(right), 'tr'));
};

// Sağlayıcı kampanya açılabilecek her oyunu listeler; backend ise katalogda
// bulunmayan ya da sitede pasif olan oyunu 400 ile reddeder (``selectable``).
// Seçilemeyen satır listede durursa yönetici oyunu seçer ve hatayı ancak
// kampanyayı açarken görür — bu yüzden liste kaynağında elenir.
export const filterCampaignGames = (items, query) => {
  const needle = normalizeSearch(query);
  return [...items]
    .filter((item) => item.selectable !== false)
    .filter((item) => {
      if (!needle) return true;
      return normalizeSearch(item.game_name).includes(needle)
        || normalizeSearch(item.game_id).includes(needle);
    })
    .sort((left, right) => {
      const leftLabel = left.game_name || `Oyun ${left.game_id}`;
      const rightLabel = right.game_name || `Oyun ${right.game_id}`;
      return leftLabel.localeCompare(rightLabel, 'tr');
    });
};

/** Listeden elenen oyunların sayısı ve gerekçesi.
 *
 * Boş bir oyun listesi tek başına hiçbir şey anlatmıyor: sağlayıcı hiç oyun
 * vermemiş de olabilir, verdiklerinin tamamı sitede pasif de olabilir. İkisi
 * farklı işler gerektirdiği için sayı ve gerekçe panelde yazılır.
 */
export const campaignGameHiddenSummary = (items) => {
  const hidden = (items || []).filter((item) => item.selectable === false);
  return {
    hidden: hidden.length,
    inactive: hidden.filter(
      (item) => item.unusable_reason === 'inactive').length,
    missing: hidden.filter(
      (item) => item.unusable_reason === 'not_in_catalog').length,
  };
};

export const campaignHiddenNotice = (items) => {
  const { hidden, inactive, missing } = campaignGameHiddenSummary(items);
  if (!hidden) return '';
  const reasons = [];
  if (inactive) {
    reasons.push(`${inactive} oyun sitede pasif (Oyun Yönetimi'nden `
      + 'aktifleştirin)');
  }
  if (missing) {
    reasons.push(`${missing} oyun site kataloğunda yok (Drakon oyunlarını `
      + 'senkronize edin)');
  }
  return `${hidden} oyun listelenmedi: ${reasons.join(', ')}.`;
};

/** Kampanya butonunun kilit sebebi; boş string "açılabilir" demektir.
 *
 * Sebep tek yerde üretilir: buton hem pasif kalmalı hem de **neden** pasif
 * olduğunu ekranda yazmalıdır. Eksik alanı yalnızca tıklandığında bir toast
 * ile söylemek, yöneticiye "buton çalışmıyor" gibi görünüyordu.
 */
export const campaignBlockReason = (form, { canWrite = true } = {}) => {
  if (!canWrite) return 'Kampanya açmak için "promo.write" yetkisi gerekiyor.';
  if (!form?.vendor) return 'Önce sağlayıcı seçin.';
  if (!form?.game_id) return 'Kampanyanın açılacağı oyunu seçin.';
  if (!form?.total_bet) return 'Spin başına bahis tutarını seçin.';
  return '';
};

/** Ödül satırında kaynak bağlanamıyorsa gerekçesi.
 *
 * İki düğme de (Drakon kampanyası / oyuncu bazlı oyun) pasifken ekranda
 * hiçbir açıklama yoktu; gerekçe yalnızca ``title`` içindeydi ve fark
 * edilmiyordu.
 */
export const prizeSourceNotice = ({
  canWrite = true, drakonConfigured = false, providerReady = false,
} = {}) => {
  if (!canWrite) {
    return {
      tone: 'warn',
      text: 'Yetkiniz yalnızca görüntüleme; freespin kaynağı bağlamak için '
        + '“promo.write” gerekiyor.',
    };
  }
  if (!drakonConfigured && !providerReady) {
    return {
      tone: 'warn',
      text: 'Freespin kaynağı yok: Oyun Kontrol → Oyun Servisleri → Drakon '
        + '→ Ayarlar bölümünden Drakon bilgilerini girin ya da freespin '
        + 'verebilen bir sağlayıcının oyunlarını senkronize edin.',
    };
  }
  if (!drakonConfigured) {
    return {
      tone: 'muted',
      text: 'Drakon bilgileri eksik (Oyun Kontrol → Oyun Servisleri → Drakon '
        + '→ Ayarlar); kampanya açılamaz.',
    };
  }
  return null;
};

export const isPrizeCampaignReady = (prize) => Boolean(
  String(prize?.campaign_code || '').trim(),
);

// Oyuncu bazlı yol (Gold Palace): sağlayıcıda önceden hiçbir şey açılmaz,
// ödüle yalnızca bir katalog kaydı bağlanır.
export const isPrizeProviderReady = (prize) => Boolean(
  String(prize?.freespin_game_id || '').trim(),
);

// Ödül freespin teslim edebiliyor mu — iki yoldan herhangi biriyle.
export const isPrizeFreespinReady = (prize) => (
  isPrizeCampaignReady(prize) || isPrizeProviderReady(prize)
);

export const canEnableAnchorWithPrizes = (prizes) => prizes.some(
  (prize) => Boolean(prize.active)
    && isPrizeFreespinReady(prize)
    && Number(prize.weight || 0) > 0,
);

/** Ödül kartındaki durum rozeti.
 *
 * Rozet tek yerde üretilir: kart, düzenleme penceresi ve özet kutusu aynı
 * kuralı okumalıdır — ayrışırsa aynı ödül için iki farklı durum yazılırdı.
 */
export const prizeStatusBadge = (prize) => {
  if (!isPrizeFreespinReady(prize)) {
    return { tone: 'is-warn', label: 'Kaynak zorunlu' };
  }
  if (prize.active) return { tone: 'is-ok', label: 'Aktif' };
  return { tone: 'is-muted', label: 'Pasif' };
};

/** Ödülün freespin kaynağının tek satırlık özeti.
 *
 * Kaynak hücresi eskiden üç düğme ve üç satır metni aynı tablo hücresine
 * sığdırmaya çalışıyordu; kart düzenine geçerken metin ve düğmeler ayrıldı ve
 * metnin kendisi buraya taşındı.
 */
export const prizeSourceSummary = (prize) => {
  if (isPrizeCampaignReady(prize)) {
    const bet = prize?.campaign_total_bet;
    return {
      kind: 'campaign',
      label: 'Drakon kampanyası',
      title: `${prize.campaign_vendor || 'Sağlayıcı'} • oyun ${
        prize.campaign_game_id || '-'}`,
      note: bet ? `Spin başına bahis ${bet}` : '',
      expiresAt: prize.campaign_expires_at || '',
    };
  }
  if (isPrizeProviderReady(prize)) {
    return {
      kind: 'game',
      label: 'Oyuncu bazlı sağlayıcı',
      title: String(prize.freespin_game_name || prize.freespin_game_id),
      note: Number(prize?.freespin_bet || 0) > 0
        ? `Spin başına ${formatMoney(prize.freespin_bet)}`
        : 'Spin başına bahis: Freespin Kontrol varsayılanı',
      expiresAt: '',
    };
  }
  return {
    kind: 'none',
    label: 'Freespin kaynağı yok',
    title: '',
    note: 'Ödül aktif edilemez ve çekilişe girmez.',
    expiresAt: '',
  };
};

/** Formda kaydedilmemiş alanlar.
 *
 * Aç/kapa anahtarları tek tıkla kaydedilir ve cevabı bütün ayar kaydını
 * döndürür; yöneticinin o sırada doldurduğu alanlar cevapla ezilmesin diye
 * hangi alanların taslakta kaldığı burada hesaplanır.
 */
export const settingsDirtyKeys = (draft, saved) => {
  if (!draft || !saved) return [];
  return SETTINGS_FORM_KEYS.filter((key) => {
    const left = draft[key];
    const right = saved[key];
    if (BOOLEAN_SETTING_KEYS.has(key)) return Boolean(left) !== Boolean(right);
    if (NUMERIC_SETTING_KEYS.has(key)) {
      return Number(left || 0) !== Number(right || 0);
    }
    return String(left ?? '') !== String(right ?? '');
  });
};

/** Sayfa çubuğunun "1–25 / 132" metni. */
export const rangeLabel = (total, skip, count) => {
  if (!total) return '0 kayıt';
  const first = skip + 1;
  const last = Math.min(skip + count, total);
  return `${formatNumber(first)}–${formatNumber(last)} / ${formatNumber(total)}`;
};

const Field = ({ label, children, hint }) => (
  <label className="anch-field">
    <span>{label}</span>
    {children}
    {hint && <span className="anch-field-hint">{hint}</span>}
  </label>
);

const Pill = ({ tone = '', children }) => (
  <span className={`anch-pill ${tone}`}>{children}</span>
);

const Stat = ({
  icon: Icon, label, value, hint, tone = 'tone-neutral', toned = false, onClick,
}) => {
  const content = (
    <>
      <span className="anch-stat-label">
        {Icon && <Icon size={12} aria-hidden="true" />} {label}
      </span>
      <span className="anch-stat-value">{value}</span>
      {hint && <span className="anch-stat-hint">{hint}</span>}
    </>
  );
  const className = `anch-stat ${tone}${toned ? ' is-toned' : ''}`;
  if (!onClick) return <div className={className}>{content}</div>;
  return (
    <button type="button" className={className} onClick={onClick}>
      {content}
    </button>
  );
};

/** Liste ekranlarının ortak sayfa çubuğu. */
const Pager = ({ total, skip, limit, count, onSkip, onLimit }) => {
  const pageCount = Math.max(1, Math.ceil(total / limit));
  const page = Math.min(Math.floor(skip / limit), pageCount - 1);
  return (
    <div className="anch-pager">
      <span className="anch-pager-info">{rangeLabel(total, skip, count)}</span>
      <label className="anch-pager-size">
        Sayfa başına
        <select
          value={limit}
          onChange={(event) => onLimit(Number(event.target.value))}
          className="admin-input"
        >
          {PAGE_SIZE_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </label>
      {pageCount > 1 && (
        <div className="anch-pager-nav">
          <button
            type="button"
            className="anch-pager-btn"
            onClick={() => onSkip(Math.max(0, skip - limit))}
            disabled={page === 0}
            aria-label="Önceki sayfa"
          >
            <ChevronLeft size={14} />
          </button>
          {pageWindow(page, pageCount).map((value, index) => (
            value === 'gap' ? (
              // eslint-disable-next-line react/no-array-index-key
              <span key={`gap-${index}`} className="anch-pager-gap">…</span>
            ) : (
              <button
                key={value}
                type="button"
                className={`anch-pager-btn${value === page ? ' is-current' : ''}`}
                onClick={() => onSkip(value * limit)}
                aria-current={value === page ? 'page' : undefined}
              >
                {value + 1}
              </button>
            )
          ))}
          <button
            type="button"
            className="anch-pager-btn"
            onClick={() => onSkip(skip + limit)}
            disabled={page >= pageCount - 1}
            aria-label="Sonraki sayfa"
          >
            <ChevronRight size={14} />
          </button>
        </div>
      )}
    </div>
  );
};

/** Liste başlığındaki arama + durum çipleri. */
const ListToolbar = ({
  search, onSearch, filters, status, onStatus,
}) => (
  <div className="anch-toolbar">
    <div className="anch-search">
      <Search size={15} aria-hidden="true" />
      <input
        type="search"
        value={search}
        onChange={(event) => onSearch(event.target.value)}
        placeholder="Üye adı ara…"
        className="admin-input pl-10 pr-10"
        aria-label="Üye adı ara"
      />
      {search && (
        <button
          type="button"
          className="anch-search-clear"
          onClick={() => onSearch('')}
          aria-label="Aramayı temizle"
        >
          <X size={13} />
        </button>
      )}
    </div>
    <div className="anch-filter-chips">
      {filters.map((filter) => (
        <button
          key={filter.id || 'all'}
          type="button"
          className={`anch-filter-chip${status === filter.id ? ' is-active' : ''}`}
          onClick={() => onStatus(filter.id)}
          aria-pressed={status === filter.id}
        >
          {filter.label}
        </button>
      ))}
    </div>
  </div>
);

const ListSkeleton = ({ rows = 5 }) => (
  <div className="anch-skeleton" role="status" aria-label="Kayıtlar yükleniyor">
    {Array.from({ length: rows }, (_, index) => (
      // eslint-disable-next-line react/no-array-index-key
      <div className="anch-skeleton-row" key={index}>
        <span /><span /><span /><span /><span /><span />
      </div>
    ))}
  </div>
);

export const CampaignCombobox = ({
  disabled = false,
  emptyMessage,
  getLabel,
  getValue,
  items,
  label,
  loading = false,
  onClear,
  onSearchChange,
  onSelect,
  placeholder,
  search,
  value,
}) => {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef(null);
  const inputId = useId();
  const resultsId = useId();
  const visibleItems = items.slice(0, 100);
  const selectedItem = items.find(
    (item) => String(getValue(item)) === String(value)) || null;
  const inputValue = search || (selectedItem ? getLabel(selectedItem) : '');
  const showPanel = open && !disabled;

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      if (!containerRef.current?.contains(event.target)) {
        setOpen(false);
        setActiveIndex(-1);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  const selectItem = (item) => {
    onSelect(item);
    setOpen(false);
    setActiveIndex(-1);
  };

  const onKeyDown = (event) => {
    if (event.key === 'Escape') {
      // Açık liste kapanır ama olay pencereye çıkmaz: aksi hâlde tek Esc
      // hem açılır listeyi hem kampanya penceresini kapatırdı.
      if (open) event.stopPropagation();
      setOpen(false);
      setActiveIndex(-1);
      return;
    }
    if (!visibleItems.length) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setOpen(true);
      setActiveIndex((index) => (index + 1) % visibleItems.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setOpen(true);
      setActiveIndex((index) => (
        index <= 0 ? visibleItems.length - 1 : index - 1));
    } else if (event.key === 'Enter' && showPanel) {
      event.preventDefault();
      selectItem(visibleItems[activeIndex >= 0 ? activeIndex : 0]);
    }
  };

  return (
    <div ref={containerRef} className="anch-field">
      <label htmlFor={inputId}>{label}</label>
      <div className="anch-search">
        <Search size={15} aria-hidden="true" />
        <input
          id={inputId}
          type="text"
          value={inputValue}
          onChange={(event) => {
            onSearchChange(event.target.value);
            setOpen(true);
            setActiveIndex(-1);
          }}
          onFocus={(event) => {
            setOpen(true);
            if (value && !search) event.currentTarget.select();
          }}
          onKeyDown={onKeyDown}
          className="admin-input pl-10 pr-10"
          placeholder={placeholder}
          disabled={disabled}
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={showPanel}
          aria-controls={resultsId}
          aria-activedescendant={activeIndex >= 0
            ? `${resultsId}-${activeIndex}` : undefined}
          autoComplete="off"
        />
        {inputValue && !disabled && (
          <button
            type="button"
            onClick={() => {
              onClear();
              setOpen(true);
              setActiveIndex(-1);
            }}
            className="anch-search-clear"
            aria-label={`${label} seçimini temizle`}
          >
            <X size={14} />
          </button>
        )}
      </div>

      {showPanel && (
        <div id={resultsId} role="listbox" className="anch-picker">
          {loading && (
            <div className="anch-picker-state">
              <Loader2 size={14} className="animate-spin" /> Yükleniyor…
            </div>
          )}
          {!loading && visibleItems.length === 0 && (
            <div className="anch-picker-state">{emptyMessage}</div>
          )}
          {!loading && visibleItems.map((item, index) => {
            const itemValue = String(getValue(item));
            const selected = itemValue === String(value);
            const active = index === activeIndex;
            return (
              <button
                id={`${resultsId}-${index}`}
                key={itemValue}
                type="button"
                role="option"
                aria-selected={selected}
                onClick={() => selectItem(item)}
                onMouseEnter={() => setActiveIndex(index)}
                className={`anch-picker-row${active || selected ? ' is-active' : ''}`}
              >
                <span className="anch-picker-text">
                  <b>{getLabel(item)}</b>
                </span>
                {selected && (
                  <CheckCircle2 size={15} className="anch-picker-check" />
                )}
              </button>
            );
          })}
          {!loading && items.length > visibleItems.length && (
            <div className="anch-picker-state">
              Daha fazla sonuç için aramayı daraltın.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const AnchorSettings = () => {
  const [settings, setSettings] = useState(null);
  // Kaydedilmiş ayarların kopyası: formun kirli olup olmadığı buna göre
  // hesaplanır ve anahtar kaydederken taslak korunur.
  const [savedSettings, setSavedSettings] = useState(null);
  const [prizes, setPrizes] = useState([]);
  const [throws, setThrows] = useState([]);
  const [throwsTotal, setThrowsTotal] = useState(0);
  const [awaitingTotal, setAwaitingTotal] = useState(0);
  const [throwsBusy, setThrowsBusy] = useState(true);
  // Kuyruktaki ödülü hangi eski hakkın engellediği ayrı bir uçtan gelir ve
  // kutu **açılmadan çağrılmaz**: sayfanın açılış maliyetine girmemeli.
  const [blockersOpen, setBlockersOpen] = useState(false);
  const [blockers, setBlockers] = useState([]);
  const [blockersBusy, setBlockersBusy] = useState(false);
  const [blockersLoaded, setBlockersLoaded] = useState(false);
  const [freebets, setFreebets] = useState([]);
  const [freebetsTotal, setFreebetsTotal] = useState(0);
  const [freebetsBusy, setFreebetsBusy] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [view, setView] = useState('prizes');
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(emptyPrize);
  const [busy, setBusy] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [confirmState, setConfirmState] = useState(null);
  const [confirmBusy, setConfirmBusy] = useState(false);

  // Liste süzgeçleri; değerler backend'in kendi parametrelerine gider.
  const [throwSearch, setThrowSearch] = useState('');
  const [throwQuery, setThrowQuery] = useState(() => ({
    username: '', status: '', skip: 0, limit: readPageSize('anchor-throws') }));
  const [freebetSearch, setFreebetSearch] = useState('');
  const [freebetQuery, setFreebetQuery] = useState(() => ({
    username: '', status: '', skip: 0, limit: readPageSize('anchor-freebets') }));
  const throwRequestId = useRef(0);
  const freebetRequestId = useRef(0);

  // Drakon free spin kampanyası kurulum durumu
  const [campaignPrize, setCampaignPrize] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [vendorsBusy, setVendorsBusy] = useState(false);
  const [limits, setLimits] = useState([]);
  const [limitsBusy, setLimitsBusy] = useState(false);
  const [vendorSearch, setVendorSearch] = useState('');
  const [gameSearch, setGameSearch] = useState('');
  const limitsRequestId = useRef(0);
  const [campaignForm, setCampaignForm] = useState({
    vendor: '', game_id: '', total_bet: '', duration_days: 7,
  });
  // Oyuncu bazlı freespin oyunu seçimi (Gold Palace). Katalog binlerce satır
  // olduğu için liste sunucudan aramayla ve sayfalı gelir.
  const [gamePrize, setGamePrize] = useState(null);
  const [gameOptions, setGameOptions] = useState([]);
  const [gameOptionsTotal, setGameOptionsTotal] = useState(0);
  const [gameOptionsBusy, setGameOptionsBusy] = useState(false);
  const [gameQuery, setGameQuery] = useState('');
  const [gameForm, setGameForm] = useState({ game_id: '', bet: '' });
  const gameRequestId = useRef(0);
  const { toast } = useToast();
  const { can } = useAdminPermissions();
  // Uçlar zaten `promo.write` istiyor; yetkisiz yöneticiye çalışmayacak bir
  // buton göstermek 403'ü formun sonuna saklamaktan başka işe yaramıyordu.
  const canWrite = can('promo.write');

  const selectedLimit = useMemo(
    () => limits.find(
      (row) => String(row.game_id) === String(campaignForm.game_id)) || null,
    [limits, campaignForm.game_id],
  );
  const filteredVendors = useMemo(
    () => filterCampaignVendors(vendors, vendorSearch),
    [vendors, vendorSearch],
  );
  const filteredLimits = useMemo(
    () => filterCampaignGames(limits, gameSearch),
    [limits, gameSearch],
  );
  const hiddenGamesNotice = useMemo(
    () => campaignHiddenNotice(limits),
    [limits],
  );
  const campaignBlocker = useMemo(
    () => campaignBlockReason(campaignForm, { canWrite }),
    [campaignForm, canWrite],
  );
  // ``settings`` ilk çizimde ``null``'dır (yükleme aşağıda beklenir); alan
  // erişimi korumasız bırakılırsa React ağacı çöker ve sayfa **bomboş**
  // açılır — hata mesajı da olmaz, yönetici "panel bozuldu" sanır.
  const sourceNotice = useMemo(
    () => prizeSourceNotice({
      canWrite,
      drakonConfigured: Boolean(settings?.drakon_configured),
      providerReady: Boolean(settings?.freespin_provider_ready),
    }),
    [canWrite, settings?.drakon_configured, settings?.freespin_provider_ready],
  );

  const dirtyKeys = useMemo(
    () => settingsDirtyKeys(settings, savedSettings),
    [settings, savedSettings],
  );

  /** Uçtan gelen ayar kaydını uygular.
   *
   * `keepDraft` açıkken yöneticinin kaydetmediği alanlar korunur: anahtar
   * kaydı bütün ayar kaydını döndürdüğü için o alanlar aksi hâlde sessizce
   * geri alınırdı.
   */
  const applySettings = useCallback((data, { keepDraft = false } = {}) => {
    const next = {
      ...data,
      anchor_video_url: data.anchor_video_url || AUTO_VIDEO_VALUE,
    };
    setSavedSettings(next);
    setSettings((current) => {
      if (!keepDraft || !current) return next;
      const draft = {};
      settingsDirtyKeys(current, next).forEach((key) => {
        draft[key] = current[key];
      });
      return { ...next, ...draft };
    });
  }, []);

  const loadCore = useCallback(async () => {
    setError('');
    try {
      const [{ data: settingsData }, { data: prizeData }] = await Promise.all([
        client.get('/admin/anchor/settings'),
        client.get('/admin/anchor/prizes'),
      ]);
      applySettings(settingsData);
      setPrizes(prizeData.items || []);
    } catch (requestError) {
      setError(
        requestError.response?.data?.detail
        || 'Çapa etkinliği ayarları alınamadı');
    } finally {
      setLoading(false);
    }
  }, [applySettings]);

  const loadThrows = useCallback(async (query) => {
    const requestId = throwRequestId.current + 1;
    throwRequestId.current = requestId;
    setThrowsBusy(true);
    try {
      const { data } = await client.get('/admin/anchor/throws', {
        params: query,
      });
      if (requestId !== throwRequestId.current) return;
      setThrows(data.items || []);
      setThrowsTotal(Number(data.total || 0));
      setAwaitingTotal(Number(data.awaiting_total || 0));
    } catch (requestError) {
      if (requestId !== throwRequestId.current) return;
      toast({
        title: 'Atışlar alınamadı',
        description: requestError.response?.data?.detail
          || 'Atış listesi okunamadı',
        variant: 'destructive',
      });
    } finally {
      if (requestId === throwRequestId.current) setThrowsBusy(false);
    }
  }, [toast]);

  const loadBlockers = useCallback(async () => {
    setBlockersBusy(true);
    try {
      const { data } = await client.get('/admin/anchor/queue-blockers');
      setBlockers(data.items || []);
      setBlockersLoaded(true);
    } catch (requestError) {
      toast({
        title: 'Bekleyen ödüller alınamadı',
        description: requestError.response?.data?.detail
          || 'Kuyruk listesi okunamadı',
        variant: 'destructive',
      });
    } finally {
      setBlockersBusy(false);
    }
  }, [toast]);

  const toggleBlockers = useCallback(() => {
    setBlockersOpen((open) => {
      // İlk açılışta bir kez okunur; sonrası yöneticinin Yenile'sine kalır.
      if (!open && !blockersLoaded && !blockersBusy) loadBlockers();
      return !open;
    });
  }, [blockersLoaded, blockersBusy, loadBlockers]);

  const loadFreebets = useCallback(async (query) => {
    const requestId = freebetRequestId.current + 1;
    freebetRequestId.current = requestId;
    setFreebetsBusy(true);
    try {
      const { data } = await client.get('/admin/anchor/freebets', {
        params: query,
      });
      if (requestId !== freebetRequestId.current) return;
      setFreebets(data.items || []);
      setFreebetsTotal(Number(data.total || 0));
    } catch (requestError) {
      if (requestId !== freebetRequestId.current) return;
      toast({
        title: 'Freebet hakları alınamadı',
        description: requestError.response?.data?.detail
          || 'Freebet listesi okunamadı',
        variant: 'destructive',
      });
    } finally {
      if (requestId === freebetRequestId.current) setFreebetsBusy(false);
    }
  }, [toast]);

  useEffect(() => { loadCore(); }, [loadCore]);
  useEffect(() => { loadThrows(throwQuery); }, [loadThrows, throwQuery]);
  useEffect(() => { loadFreebets(freebetQuery); }, [loadFreebets, freebetQuery]);

  // Arama her tuşta sunucuya gitmesin; ilk çizimde de tetiklenmesin diye
  // değer zaten aynıysa state değiştirilmez (aksi hâlde effect döngüye girer).
  useEffect(() => {
    const timer = setTimeout(() => {
      setThrowQuery((current) => (
        current.username === throwSearch.trim()
          ? current
          : { ...current, username: throwSearch.trim(), skip: 0 }));
    }, 350);
    return () => clearTimeout(timer);
  }, [throwSearch]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setFreebetQuery((current) => (
        current.username === freebetSearch.trim()
          ? current
          : { ...current, username: freebetSearch.trim(), skip: 0 }));
    }, 350);
    return () => clearTimeout(timer);
  }, [freebetSearch]);

  const refreshAll = useCallback(() => {
    loadCore();
    loadThrows(throwQuery);
    loadFreebets(freebetQuery);
  }, [loadCore, loadThrows, loadFreebets, throwQuery, freebetQuery]);

  const totalWeight = useMemo(
    () => prizes
      .filter((prize) => prize.active && isPrizeFreespinReady(prize))
      .reduce((sum, prize) => sum + Number(prize.weight || 0), 0),
    [prizes],
  );
  const canEnableAnchor = useMemo(
    () => canEnableAnchorWithPrizes(prizes),
    [prizes],
  );
  const activePrizeCount = useMemo(
    () => prizes.filter(
      (prize) => prize.active && isPrizeFreespinReady(prize)).length,
    [prizes],
  );
  const sourcelessCount = useMemo(
    () => prizes.filter((prize) => !isPrizeFreespinReady(prize)).length,
    [prizes],
  );
  const activeFreebets = useMemo(
    () => freebets.filter((row) => row.status === 'active').length,
    [freebets],
  );

  const saveSettings = async (patch) => {
    setSavingSettings(true);
    const payload = patch || {
      anchor_title: settings.anchor_title || '',
      anchor_description: settings.anchor_description || '',
      anchor_video_url: settings.anchor_video_url || '',
      anchor_cooldown_hours: Number(settings.anchor_cooldown_hours || 0),
      anchor_min_deposit: Number(settings.anchor_min_deposit || 0),
      anchor_require_telegram: Boolean(settings.anchor_require_telegram),
      anchor_single_ip: Boolean(settings.anchor_single_ip),
      anchor_freespin_auto_delivery: Boolean(
        settings.anchor_freespin_auto_delivery),
      anchor_freebet_min_odd: Number(settings.anchor_freebet_min_odd || 1),
      anchor_freebet_expiry_days: Number(
        settings.anchor_freebet_expiry_days || 0),
    };
    try {
      const { data } = await client.patch('/admin/anchor/settings', payload);
      // Anahtar kaydında formun taslağı korunur; tam kayıtta korunacak bir
      // taslak zaten kalmamıştır.
      applySettings(data, { keepDraft: Boolean(patch) });
      toast({ title: 'Ayarlar kaydedildi' });
      return true;
    } catch (requestError) {
      toast({
        title: 'Kaydedilemedi',
        description: requestError.response?.data?.detail
          || 'Ayarlar kaydedilemedi',
        variant: 'destructive',
      });
      return false;
    } finally {
      setSavingSettings(false);
    }
  };

  const openNew = () => {
    setForm({ ...emptyPrize, order: prizes.length });
    setModal('create');
  };

  const openEdit = (prize) => {
    setForm({
      ...prize,
      active: isPrizeFreespinReady(prize) && Boolean(prize.active),
    });
    setModal('edit');
  };

  const savePrize = async () => {
    setBusy(true);
    const payload = {
      value: Number(form.value || 0),
      weight: Number(form.weight || 0),
      color: form.color,
      active: Boolean(form.active),
      order: Number(form.order || 0),
    };
    try {
      if (modal === 'create') {
        await client.post('/admin/anchor/prizes', payload);
      } else {
        await client.patch(`/admin/anchor/prizes/${form.id}`, payload);
      }
      setModal(null);
      await loadCore();
      toast({ title: 'Ödül kaydedildi' });
    } catch (requestError) {
      toast({
        title: 'Kaydedilemedi',
        description: requestError.response?.data?.detail || 'Ödül kaydedilemedi',
        variant: 'destructive',
      });
    } finally {
      setBusy(false);
    }
  };

  const deletePrize = (prize) => setConfirmState({
    title: 'Ödül silinsin mi?',
    description: `${prize.value} değerli ödül kalıcı olarak silinecek. `
      + 'Ağırlıklı son ödülse etkinlik de otomatik kapanır.',
    confirmLabel: 'Sil',
    tone: 'danger',
    run: async () => {
      const { data } = await client.delete(`/admin/anchor/prizes/${prize.id}`);
      await loadCore();
      toast({
        title: 'Ödül silindi',
        description: data.auto_disabled
          ? 'Ağırlıklı ödül kalmadığı için etkinlik otomatik kapatıldı'
          : undefined,
      });
    },
    errorTitle: 'Silinemedi',
    errorText: 'Ödül silinemedi',
  });

  const seedDefaults = async () => {
    try {
      await client.post('/admin/anchor/prizes/defaults');
      await loadCore();
      toast({ title: 'Örnek ödül seti yüklendi' });
    } catch (requestError) {
      toast({
        title: 'Yüklenemedi',
        description: requestError.response?.data?.detail
          || 'Örnek set yüklenemedi',
        variant: 'destructive',
      });
    }
  };

  // ------------- Oyuncu bazlı freespin oyunu (Gold Palace) ------------- //
  // Drakon'un aksine kampanya açılmaz: ödüle bir katalog kaydı bağlanır, hak
  // oyuncu ödülü kazandığı anda tanımlanır. Liste sunucudan gelir çünkü
  // katalog binlerce satırdır; tarayıcıya ilk sayfayı çekip süzmek eksik
  // sonuç üretir.

  const loadFreespinGames = useCallback(async (query) => {
    const requestId = gameRequestId.current + 1;
    gameRequestId.current = requestId;
    setGameOptionsBusy(true);
    try {
      const { data } = await client.get('/admin/freespin/games', {
        params: { q: query || '', limit: 50 },
      });
      if (requestId !== gameRequestId.current) return;
      setGameOptions(data.items || []);
      setGameOptionsTotal(Number(data.total || 0));
    } catch (requestError) {
      if (requestId !== gameRequestId.current) return;
      setGameOptions([]);
      setGameOptionsTotal(0);
      toast({
        title: 'Oyunlar alınamadı',
        description: requestError.response?.data?.detail
          || 'Freespin verebilen oyun listesi okunamadı',
        variant: 'destructive',
      });
    } finally {
      if (requestId === gameRequestId.current) setGameOptionsBusy(false);
    }
  }, [toast]);

  const openGamePicker = (prize) => {
    setGamePrize(prize);
    setGameQuery('');
    setGameForm({
      game_id: String(prize.freespin_game_id || ''),
      bet: prize.freespin_bet ? String(prize.freespin_bet) : '',
    });
    setGameOptions([]);
    setGameOptionsTotal(0);
    // Listeyi burada çekmiyoruz: aşağıdaki debounce'lu etki pencere açılınca
    // zaten çalışır, iki kez istek atmasın.
    setGameOptionsBusy(true);
  };

  // Arama her tuşta sunucuya gitmesin; pencere kapalıyken hiç çalışmaz.
  useEffect(() => {
    if (!gamePrize) return undefined;
    const timer = setTimeout(() => loadFreespinGames(gameQuery), 300);
    return () => clearTimeout(timer);
  }, [gamePrize, gameQuery, loadFreespinGames]);

  const saveFreespinGame = async () => {
    if (!gameForm.game_id) {
      toast({ title: 'Önce bir oyun seçin' });
      return;
    }
    setBusy(true);
    try {
      await client.put(
        `/admin/anchor/prizes/${gamePrize.id}/freespin-game`,
        { game_id: gameForm.game_id, bet: Number(gameForm.bet || 0) },
      );
      setGamePrize(null);
      await loadCore();
      toast({
        title: 'Freespin oyunu bağlandı',
        description: `${gamePrize.value} spin bu oyunda tanımlanacak. Ödülü `
          + 'aktif etmeyi unutmayın.',
      });
    } catch (requestError) {
      toast({
        title: 'Oyun bağlanamadı',
        description: requestError.response?.data?.detail
          || 'Ödüle freespin oyunu bağlanamadı',
        variant: 'destructive',
      });
    } finally {
      setBusy(false);
    }
  };

  const clearFreespinGame = (prize) => setConfirmState({
    title: 'Oyun bağlantısı kaldırılsın mı?',
    description: `${prize.value} ödülünün freespin oyunu kaldırılacak. `
      + 'Kaynağı olmayan ödül pasife alınır ve çekilişe girmez.',
    confirmLabel: 'Kaldır',
    tone: 'danger',
    run: async () => {
      const { data } = await client.delete(
        `/admin/anchor/prizes/${prize.id}/freespin-game`);
      await loadCore();
      toast({
        title: 'Oyun bağlantısı kaldırıldı',
        description: data.auto_disabled
          ? 'Kullanılabilir ödül kalmadığı için etkinlik kapatıldı'
          : 'Ödül otomatik olarak pasife alındı',
      });
    },
    errorTitle: 'Kaldırılamadı',
    errorText: 'Oyun bağlantısı kaldırılamadı',
  });

  // ------------------------- Drakon kampanyası ------------------------- //
  // Free spin adedi kampanyaya gömülü olduğu için her ödül değeri kendi
  // kampanyasını ister; kampanya panelden açılır, ödül anında oyuncu eklenir.

  const openCampaign = async (prize) => {
    setCampaignPrize(prize);
    setLimits([]);
    setVendorSearch('');
    setGameSearch('');
    setCampaignForm({ vendor: '', game_id: '', total_bet: '', duration_days: 7 });
    if (vendors.length) return;
    setVendorsBusy(true);
    try {
      const { data } = await client.get('/admin/anchor/campaign/vendors');
      setVendors(data.items || []);
    } catch (requestError) {
      toast({
        title: 'Sağlayıcılar alınamadı',
        description: requestError.response?.data?.detail
          || 'Drakon sağlayıcı listesi okunamadı',
        variant: 'destructive',
      });
    } finally {
      setVendorsBusy(false);
    }
  };

  const selectVendor = async (vendor) => {
    const requestId = limitsRequestId.current + 1;
    limitsRequestId.current = requestId;
    setVendorSearch('');
    setGameSearch('');
    setCampaignForm((current) => ({
      ...current, vendor, game_id: '', total_bet: '',
    }));
    setLimits([]);
    if (!vendor) {
      setLimitsBusy(false);
      return;
    }
    setLimitsBusy(true);
    try {
      const { data } = await client.get('/admin/anchor/campaign/limits', {
        params: { vendor },
      });
      if (requestId === limitsRequestId.current) setLimits(data.items || []);
    } catch (requestError) {
      if (requestId !== limitsRequestId.current) return;
      toast({
        title: 'Limitler alınamadı',
        description: requestError.response?.data?.detail
          || 'Sağlayıcı bahis limitleri okunamadı',
        variant: 'destructive',
      });
    } finally {
      if (requestId === limitsRequestId.current) setLimitsBusy(false);
    }
  };

  const searchVendors = (query) => {
    setVendorSearch(query);
    if (!campaignForm.vendor) return;
    limitsRequestId.current += 1;
    setCampaignForm((current) => ({
      ...current, vendor: '', game_id: '', total_bet: '',
    }));
    setGameSearch('');
    setLimits([]);
    setLimitsBusy(false);
  };

  const searchGames = (query) => {
    setGameSearch(query);
    if (!campaignForm.game_id) return;
    setCampaignForm((current) => ({
      ...current, game_id: '', total_bet: '',
    }));
  };

  const selectGame = (game) => {
    setGameSearch('');
    setCampaignForm((current) => ({
      ...current,
      game_id: game ? String(game.game_id) : '',
      total_bet: '',
    }));
  };

  const createCampaign = async () => {
    if (campaignBlocker) {
      toast({ title: 'Kampanya açılamıyor', description: campaignBlocker });
      return;
    }
    setBusy(true);
    try {
      await client.post(
        `/admin/anchor/prizes/${campaignPrize.id}/campaign`, campaignForm);
      setCampaignPrize(null);
      await loadCore();
      toast({
        title: 'Kampanya oluşturuldu',
        description: `${campaignPrize.value} free spin kampanyası açıldı ve ödül aktif edildi`,
      });
    } catch (requestError) {
      toast({
        title: 'Kampanya oluşturulamadı',
        description: requestError.response?.data?.detail
          || 'Sağlayıcı kampanyayı açmadı',
        variant: 'destructive',
      });
    } finally {
      setBusy(false);
    }
  };

  const checkCampaign = async (prize) => {
    try {
      const { data } = await client.get(
        `/admin/anchor/prizes/${prize.id}/campaign`);
      const campaign = data.campaign || {};
      toast({
        title: `Kampanya ${campaign.status || 'durumu okunamadı'}`,
        description: `${(campaign.players || []).length} oyuncu • bitiş `
          + `${campaign.expires_at || '-'}`,
      });
    } catch (requestError) {
      toast({
        title: 'Durum alınamadı',
        description: requestError.response?.data?.detail
          || 'Kampanya sağlayıcıda bulunamadı',
        variant: 'destructive',
      });
    }
  };

  const cancelCampaign = (prize) => setConfirmState({
    title: 'Kampanya iptal edilsin mi?',
    description: `${prize.value} free spin kampanyası sağlayıcıda iptal `
      + 'edilecek. İptal edilen kampanyaya yeni oyuncu eklenemez ve ödül '
      + 'pasife alınır.',
    confirmLabel: 'Kampanyayı iptal et',
    tone: 'danger',
    run: async () => {
      const { data } = await client.delete(
        `/admin/anchor/prizes/${prize.id}/campaign`);
      await loadCore();
      toast({
        title: 'Kampanya iptal edildi',
        description: data.provider_note
          || (data.auto_disabled
            ? 'Ödül pasife alındı; kullanılabilir kampanya kalmadığı için etkinlik kapatıldı'
            : 'Ödül otomatik olarak pasife alındı'),
      });
    },
    errorTitle: 'İptal edilemedi',
    errorText: 'Kampanya iptal edilemedi',
  });

  const deliverThrow = (row) => setConfirmState({
    title: 'Freespin şimdi tanımlansın mı?',
    description: `${row.username} için ${row.spin_count} freespin sağlayıcıda `
      + 'tanımlanacak. İşlem ödülün kaynağına göre yürütülür: Drakon '
      + 'kampanyasına eklenir ya da bağlı oyunda doğrudan açılır.',
    confirmLabel: 'Şimdi tanımla',
    tone: 'primary',
    run: async () => {
      await client.post(`/admin/anchor/throws/${row.id}/deliver`, { note: '' });
      await loadThrows(throwQuery);
      toast({ title: 'Freespin sağlayıcıda tanımlandı' });
    },
    errorTitle: 'Freespin tanımlanamadı',
    errorText: 'Sağlayıcı işlemi tamamlanamadı',
  });

  const runConfirm = async () => {
    if (!confirmState) return;
    setConfirmBusy(true);
    try {
      await confirmState.run();
      setConfirmState(null);
    } catch (requestError) {
      toast({
        title: confirmState.errorTitle || 'İşlem tamamlanamadı',
        description: requestError.response?.data?.detail
          || confirmState.errorText || 'Beklenmeyen bir hata oluştu',
        variant: 'destructive',
      });
    } finally {
      setConfirmBusy(false);
    }
  };

  // Pencereler Esc ile kapanır. İşlem sürerken kapatılmaz: yarıda bırakılan
  // bir kampanya isteği sağlayıcıda açılmış olabilir ve pencere kapanınca
  // yönetici sonucu hiç görmezdi.
  const closeModals = useCallback(() => {
    setModal(null);
    setCampaignPrize(null);
    setGamePrize(null);
  }, []);

  useEffect(() => {
    if (!modal && !campaignPrize && !gamePrize) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape' && !busy) closeModals();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [modal, campaignPrize, gamePrize, busy, closeModals]);

  const openQueue = () => {
    setView('throws');
    setThrowSearch('');
    setThrowQuery((current) => ({
      ...current, username: '', status: 'awaiting_provider', skip: 0,
    }));
  };

  if (loading) {
    return (
      <div className="admin-page anch-page">
        <div className="anch-panel"><ListSkeleton rows={6} /></div>
      </div>
    );
  }

  if (error || !settings) {
    return (
      <div className="admin-page anch-page">
        <AdminError message={error} onRetry={loadCore} />
      </div>
    );
  }

  const enabled = Boolean(settings.anchor_enabled);
  const autoDelivery = Boolean(settings.anchor_freespin_auto_delivery);
  const providerNames = (settings.freespin_provider_names || []).join(', ');
  const dirty = dirtyKeys.length > 0;

  const tabs = [
    { id: 'prizes', label: 'Ödüller', icon: Gift, count: prizes.length },
    {
      id: 'throws',
      label: 'Atışlar',
      icon: ListChecks,
      count: throwsTotal,
      badge: awaitingTotal || 0,
    },
    { id: 'freebets', label: 'Freebet Hakları', icon: Ticket, count: freebetsTotal },
    { id: 'settings', label: 'Ayarlar', icon: Settings2, dot: dirty },
  ];

  return (
    <div className="admin-page anch-page">
      <header className="anch-head">
        <div className="anch-head-icon">
          <Anchor size={21} aria-hidden="true" />
        </div>
        <div className="anch-head-text">
          <h1>Çapa Etkinliği</h1>
          <p>
            Günde bir ücretsiz ödül. Çıkan <b>değer sunucuda</b> seçilir,
            ödülün türünü (freespin ya da freebet) oyuncu belirler. Aynı değer
            iki anlama gelir: 20 çapa = 20 freespin ya da 20 ₺ freebet.
          </p>
        </div>
        <div className="anch-head-actions">
          <button
            type="button"
            onClick={refreshAll}
            className="admin-btn-secondary"
          >
            <RefreshCw size={14} aria-hidden="true" /> Yenile
          </button>
          <button
            type="button"
            onClick={openNew}
            disabled={!canWrite}
            title={canWrite ? '' : 'Ödül eklemek için "promo.write" yetkisi gerekiyor'}
            className="admin-btn-primary"
          >
            <Plus size={14} aria-hidden="true" /> Yeni Ödül
          </button>
        </div>
      </header>

      {/* ------------------------------------------------- yayın durumu --- */}
      <section className={`anch-live ${enabled ? 'is-on' : 'is-off'}`}>
        <span className="anch-live-mark" aria-hidden="true">
          <Power size={18} />
        </span>
        <div className="anch-live-text">
          <div className="anch-live-title">
            {enabled ? 'Etkinlik yayında' : 'Etkinlik kapalı'}
            <Pill tone={enabled ? 'is-ok' : 'is-muted'}>
              {enabled ? 'Oyuncuya açık' : 'Gizli'}
            </Pill>
          </div>
          <p>
            {enabled
              ? 'Oyuncular /capa sayfasından çapayı atabiliyor. Kapatırsanız '
                + 'sayfa “şu anda kapalı” bilgisi gösterir ve menüden gizlenir.'
              : '/capa sayfası ziyaretçilere “şu anda kapalı” bilgisi '
                + 'gösteriyor ve menüde görünmüyor.'}
          </p>
        </div>
        <button
          type="button"
          onClick={() => saveSettings({ anchor_enabled: !enabled })}
          disabled={savingSettings || !canWrite || (!enabled && !canEnableAnchor)}
          title={!canWrite
            ? 'Bu işlem için "promo.write" yetkisi gerekiyor'
            : (!enabled && !canEnableAnchor
              ? 'Önce ağırlığı sıfırdan büyük en az bir ödüle freespin kaynağı bağlayın'
              : '')}
          className="anch-live-btn"
        >
          {savingSettings
            ? <Loader2 size={15} className="animate-spin" aria-hidden="true" />
            : <Power size={15} aria-hidden="true" />}
          {enabled ? 'Etkinliği kapat' : 'Etkinliği yayına al'}
        </button>
      </section>

      {!canEnableAnchor && !enabled && (
        <div className="anch-note is-warn">
          <AlertTriangle size={15} aria-hidden="true" />
          <div className="anch-note-body">
            Etkinliği açmak için <b>ağırlığı sıfırdan büyük, aktif ve freespin
            kaynağı bağlanmış</b> en az bir ödül gerekir. Kaynaksız ödüller
            aktif edilemez — ödül kartındaki <b>Drakon Kampanyası</b> ya da
            <b> Oyun Seç</b> düğmesini kullanın.
          </div>
        </div>
      )}

      {awaitingTotal > 0 && (
        <div className="anch-note is-warn">
          <Zap size={15} aria-hidden="true" />
          <div className="anch-note-body">
            <b>{awaitingTotal}</b> freespin ödülü sağlayıcıda tanımlanmayı
            bekliyor. Oyuncu ödülü kazandı ama hakkı henüz açılmadı.
          </div>
          <span className="anch-note-action">
            <button type="button" onClick={openQueue} className="admin-btn-secondary">
              <ListChecks size={13} aria-hidden="true" /> Kuyruğu aç
            </button>
          </span>
        </div>
      )}

      {/* ------------------------------------------------------- özet ---- */}
      <div className="anch-stats">
        <Stat
          icon={Gift}
          label="Çekilişteki ödül"
          value={`${activePrizeCount} / ${prizes.length}`}
          hint={sourcelessCount
            ? `${sourcelessCount} ödülün kaynağı yok`
            : 'Hepsinin kaynağı bağlı'}
          tone={sourcelessCount ? 'tone-warn' : 'tone-ok'}
          toned={Boolean(sourcelessCount)}
        />
        <Stat
          icon={Percent}
          label="Toplam ağırlık"
          value={formatNumber(totalWeight)}
          hint="Olasılık, ağırlığın bu toplama oranıdır"
          tone="tone-info"
        />
        <Stat
          icon={Zap}
          label="Sağlayıcı kuyruğu"
          value={formatNumber(awaitingTotal)}
          hint={awaitingTotal ? 'Tanımlanmayı bekliyor' : 'Bekleyen ödül yok'}
          tone={awaitingTotal ? 'tone-warn' : 'tone-neutral'}
          toned={Boolean(awaitingTotal)}
          onClick={awaitingTotal ? openQueue : undefined}
        />
        <Stat
          icon={Activity}
          label="Toplam atış"
          value={formatNumber(throwsTotal)}
          hint={throwQuery.status || throwQuery.username
            ? 'Süzgeçle daraltıldı'
            : 'Bugüne kadarki bütün atışlar'}
          tone="tone-deep"
        />
        <Stat
          icon={Ticket}
          label="Freebet hakkı"
          value={formatNumber(freebetsTotal)}
          hint={`Bu sayfada ${activeFreebets} kullanılabilir`}
          tone="tone-neutral"
        />
      </div>

      {/* --------------------------------------------- freespin kaynakları */}
      <div className="anch-sources">
        <div className={`anch-source${settings.drakon_configured ? ' is-ready' : ''}`}>
          <span className="anch-source-icon" aria-hidden="true">
            <Zap size={16} />
          </span>
          <div className="anch-source-body">
            <div className="anch-source-title">
              Drakon kampanyası
              <Pill tone={settings.drakon_configured ? 'is-ok' : 'is-warn'}>
                {settings.drakon_configured ? 'Hazır' : 'Bilgi eksik'}
              </Pill>
            </div>
            <p>
              {settings.drakon_configured
                ? 'Adet kampanyaya gömülüdür: her ödül değeri kendi '
                  + 'kampanyasını ister.'
                : 'Oyun Kontrol → Oyun Servisleri → Drakon → Ayarlar '
                  + 'bölümünden agent bilgilerini girin.'}
            </p>
          </div>
        </div>
        <div className={`anch-source${settings.freespin_provider_ready ? ' is-ready' : ''}`}>
          <span className="anch-source-icon" aria-hidden="true">
            <Gamepad2 size={16} />
          </span>
          <div className="anch-source-body">
            <div className="anch-source-title">
              Oyuncu bazlı sağlayıcı
              <Pill tone={settings.freespin_provider_ready ? 'is-ok' : 'is-warn'}>
                {settings.freespin_provider_ready ? 'Hazır' : 'Oyun yok'}
              </Pill>
            </div>
            <p>
              {settings.freespin_provider_ready
                ? `${formatNumber(settings.freespin_provider_games)} uygun oyun`
                  + `${providerNames ? ` • ${providerNames}` : ''}. Kampanya `
                  + 'gerekmez, hak ödül anında tanımlanır.'
                : 'Freespin verebilen aktif oyun yok; oyunları senkronize edin '
                  + 've Freespin Kontrol sayfasını açın.'}
            </p>
          </div>
        </div>
      </div>

      {/* ---------------------------------------------------- sekmeler --- */}
      <nav className="anch-tabs" aria-label="Çapa etkinliği bölümleri">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setView(tab.id)}
            className={`anch-tab${view === tab.id ? ' is-active' : ''}`}
            aria-current={view === tab.id ? 'page' : undefined}
          >
            <tab.icon size={14} aria-hidden="true" />
            {tab.label}
            {tab.badge > 0 && (
              <span className="anch-tab-count is-warn">{tab.badge}</span>
            )}
            {!tab.badge && tab.count > 0 && (
              <span className="anch-tab-count">{formatNumber(tab.count)}</span>
            )}
            {tab.dot && <span className="anch-pill is-warn">Kaydedilmedi</span>}
          </button>
        ))}
      </nav>

      {/* ------------------------------------------------------- ödüller - */}
      {view === 'prizes' && (
        <>
          <section className="anch-panel">
            <div className="anch-panel-head">
              <span className="anch-panel-title">
                <Gift size={15} aria-hidden="true" /> Ödül değerleri
              </span>
              <span className="anch-panel-meta">
                {prizes.length
                  ? `${prizes.length} ödül • toplam ağırlık ${totalWeight}`
                  : 'Henüz ödül yok'}
              </span>
            </div>

            {prizes.length === 0 ? (
              <AdminEmpty
                title="Henüz ödül tanımlanmamış"
                description="Çapadan çıkacak değerleri buradan tanımlarsınız. Örnek set 20/30/40 değerleriyle başlar; değerleri sonradan değiştirebilirsiniz."
                action={canWrite ? (
                  <button type="button" onClick={seedDefaults} className="admin-btn-primary">
                    <Sparkles size={14} aria-hidden="true" /> Örnek seti yükle
                  </button>
                ) : null}
              />
            ) : (
              <div className="anch-prizes">
                {prizes.map((prize) => {
                  const badge = prizeStatusBadge(prize);
                  const source = prizeSourceSummary(prize);
                  const chance = Number(prize.chance_percent || 0);
                  return (
                    <article
                      key={prize.id}
                      className={`anch-prize${prize.active ? '' : ' is-idle'}`}
                      style={{ '--anch-prize-color': prize.color }}
                    >
                      <div className="anch-prize-top">
                        <div className="anch-prize-value">
                          <b>{prize.value}</b>
                          <span>freespin<br />veya ₺ freebet</span>
                        </div>
                        <div className="anch-prize-badges">
                          <Pill tone={badge.tone}>{badge.label}</Pill>
                        </div>
                      </div>

                      <div className="anch-prize-chance">
                        <div className="anch-prize-chance-top">
                          <b>%{chance}</b>
                          çıkma olasılığı
                          <span>ağırlık {prize.weight}</span>
                        </div>
                        <div
                          className="anch-prize-bar"
                          role="img"
                          aria-label={`Çıkma olasılığı yüzde ${chance}`}
                        >
                          <i style={{ width: `${Math.min(100, chance)}%` }} />
                        </div>
                      </div>

                      <div className={`anch-prize-source${
                        source.kind === 'none' ? '' : ' is-ready'}`}
                      >
                        <div className="anch-prize-source-head">
                          {source.kind === 'campaign' && <Zap size={12} />}
                          {source.kind === 'game' && <Gamepad2 size={12} />}
                          {source.kind === 'none' && <CircleSlash size={12} />}
                          {source.label}
                        </div>
                        <div className="anch-prize-source-lines">
                          {source.title && <b>{source.title}</b>}
                          {source.note && <small>{source.note}</small>}
                          {source.expiresAt && (
                            <small>bitiş {formatDate(source.expiresAt)}</small>
                          )}
                        </div>
                        <div className="anch-prize-source-actions">
                          {source.kind === 'campaign' && (
                            <>
                              <button
                                type="button"
                                onClick={() => checkCampaign(prize)}
                                className="anch-mini"
                              >
                                <Activity size={12} aria-hidden="true" /> Durum
                              </button>
                              <button
                                type="button"
                                onClick={() => cancelCampaign(prize)}
                                disabled={!canWrite}
                                className="anch-mini is-danger"
                              >
                                <Unlink size={12} aria-hidden="true" /> İptal
                              </button>
                            </>
                          )}
                          {source.kind === 'game' && (
                            <>
                              <button
                                type="button"
                                onClick={() => openGamePicker(prize)}
                                disabled={!canWrite}
                                className="anch-mini"
                              >
                                <Edit3 size={12} aria-hidden="true" /> Değiştir
                              </button>
                              <button
                                type="button"
                                onClick={() => clearFreespinGame(prize)}
                                disabled={!canWrite}
                                className="anch-mini is-danger"
                              >
                                <Unlink size={12} aria-hidden="true" /> Kaldır
                              </button>
                            </>
                          )}
                          {source.kind === 'none' && (
                            <>
                              <button
                                type="button"
                                onClick={() => openCampaign(prize)}
                                disabled={!settings.drakon_configured || !canWrite}
                                className="anch-mini is-primary"
                                title={!canWrite
                                  ? 'Kampanya açmak için "promo.write" yetkisi gerekiyor'
                                  : settings.drakon_configured
                                    ? 'Drakon: adet kampanyaya gömülüdür'
                                    : 'Önce Oyun Kontrol → Oyun Servisleri → Drakon → Ayarlar bölümünden Drakon bilgilerini girin'}
                              >
                                <Zap size={12} aria-hidden="true" /> Drakon Kampanyası
                              </button>
                              <button
                                type="button"
                                onClick={() => openGamePicker(prize)}
                                disabled={!settings.freespin_provider_ready || !canWrite}
                                className="anch-mini is-primary"
                                title={!canWrite
                                  ? 'Freespin oyunu bağlamak için "promo.write" yetkisi gerekiyor'
                                  : settings.freespin_provider_ready
                                    ? 'Gold Palace: hak oyuncuya doğrudan tanımlanır'
                                    : 'Freespin verebilen aktif oyun yok; önce oyunları senkronize edin ve Freespin Kontrol sayfasını açın'}
                              >
                                <Gamepad2 size={12} aria-hidden="true" /> Oyun Seç
                              </button>
                              {/* Sebep tooltip'te kalırsa yönetici düğmeyi
                                  bozuk sanıyor; ekranda da yazılır. */}
                              {sourceNotice && (
                                <span className={`anch-hint${
                                  sourceNotice.tone === 'warn' ? ' anch-warn-text' : ''}`}
                                >
                                  {sourceNotice.text}
                                </span>
                              )}
                            </>
                          )}
                        </div>
                      </div>

                      <div className="anch-prize-foot">
                        <span className="anch-order">sıra {prize.order}</span>
                        <button
                          type="button"
                          onClick={() => openEdit(prize)}
                          disabled={!canWrite}
                          className="anch-mini"
                        >
                          <Edit3 size={12} aria-hidden="true" /> Düzenle
                        </button>
                        <button
                          type="button"
                          onClick={() => deletePrize(prize)}
                          disabled={!canWrite || isPrizeCampaignReady(prize)}
                          title={isPrizeCampaignReady(prize)
                            ? 'Önce bağlı kampanyayı iptal edin'
                            : 'Ödülü sil'}
                          className="anch-mini is-danger is-icon"
                          aria-label={`${prize.value} değerli ödülü sil`}
                        >
                          <Trash2 size={12} aria-hidden="true" />
                        </button>
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </section>

          <div className="anch-help">
            <div className="anch-help-card">
              <Zap size={15} aria-hidden="true" />
              <p>
                <b>Her ödül değeri bir freespin kaynağı ister</b>
                Kaynağı olmayan değerden freespin seçen oyuncunun ödülü iptal
                edilmez, kuyruğa düşer. <b>Drakon kampanyası</b>: adet
                kampanyaya gömülüdür, her değerin kendi kampanyası olmalıdır ve
                süresi dolunca yenisi açılır. <b>Oyun Seç</b>: oyuncu bazlı
                freespin veren sağlayıcılarda kampanya gerekmez, hak ödül
                anında tanımlanır ve değeri istediğiniz zaman değiştirirsiniz.
              </p>
            </div>
            <div className="anch-help-card">
              <Coins size={15} aria-hidden="true" />
              <p>
                <b>Haftalık set değişimi</b>
                Değerler kodda sabit değildir: 20/30/40 → 40/50/60 geçişini bu
                kartları düzenleyerek yaparsınız. Kampanyalı ödülde değer
                kilitlidir — önce kampanyayı iptal edin, sonra yeni değerle
                yeniden açın.
              </p>
            </div>
          </div>
        </>
      )}

      {/* -------------------------------------------------------- atışlar */}
      {view === 'throws' && (
        <>
          <section className="anch-panel">
            <div className="anch-panel-head">
              <span className="anch-panel-title">
                <ListChecks size={15} aria-hidden="true" /> Atışlar
              </span>
              <span className="anch-panel-meta">
                {throwsBusy
                  ? 'Yükleniyor…'
                  : rangeLabel(throwsTotal, throwQuery.skip, throws.length)}
              </span>
            </div>

            <ListToolbar
              search={throwSearch}
              onSearch={setThrowSearch}
              filters={THROW_STATUS_FILTERS}
              status={throwQuery.status}
              onStatus={(status) => setThrowQuery(
                (current) => ({ ...current, status, skip: 0 }))}
            />

            {awaitingTotal > 0 && (
              <div className={`anch-blockers${blockersOpen ? ' is-open' : ''}`}>
                <button
                  type="button"
                  className="anch-blockers-head"
                  onClick={toggleBlockers}
                  aria-expanded={blockersOpen}
                >
                  <ChevronDown size={14} aria-hidden="true" className="anch-blockers-caret" />
                  <AlertTriangle size={14} aria-hidden="true" />
                  <b>Kuyrukta bekleyen {formatNumber(awaitingTotal)} ödül</b>
                  <span>— hangi eski hakkın engellediğini görmek için açın</span>
                </button>

                {blockersOpen && (
                  <div className="anch-blockers-body">
                    <div className="anch-blockers-note">
                      Sağlayıcı, oyuncuya <b>öncekisi kullanılmadan ikinci bir
                      freespin hakkı tanımlamıyor</b>. Üye aşağıdaki hakkı
                      oynadıktan sonra <b>Şimdi Tanımla</b> çalışır. Liste her
                      açılışta tazelenir; süzgeçten ve sayfadan bağımsızdır.
                      <button
                        type="button"
                        className="anch-mini"
                        onClick={loadBlockers}
                        disabled={blockersBusy}
                      >
                        <RefreshCw size={12} aria-hidden="true" /> Yenile
                      </button>
                    </div>

                    {blockersBusy && <ListSkeleton rows={3} />}

                    {!blockersBusy && blockers.length === 0 && (
                      <p className="anch-dim">Kuyrukta bekleyen ödül yok.</p>
                    )}

                    {!blockersBusy && blockers.length > 0 && (
                      <ul className="anch-blockers-list">
                        {blockers.map((row) => {
                          const when = splitDate(row.created_at);
                          const blocker = row.blocker;
                          const since = blocker
                            ? splitDate(blocker.granted_at) : null;
                          return (
                            <li key={row.id}>
                              <span className="anch-blockers-user">
                                {row.user_id ? (
                                  <Link to={`/admin/users/${row.user_id}`}>
                                    {row.username || '—'}
                                  </Link>
                                ) : (row.username || '—')}
                                <small>
                                  {formatNumber(row.spin_count || row.prize_value || 0)} spin
                                  {' · '}{when.day} tarihli atış
                                </small>
                              </span>
                              <span className="anch-blockers-cause">
                                {blocker ? (
                                  <>
                                    <b>{blocker.label}</b>
                                    {blocker.note ? ` (${blocker.note})` : ''}
                                    {' · '}
                                    {formatNumber(blocker.count || 0)} spin
                                    {since ? ` · ${since.day}` : ''}
                                    {blocker.game ? ` · ${blocker.game}` : ''}
                                  </>
                                ) : (
                                  <i>kayıtlarımızda bloke eden hak görünmüyor</i>
                                )}
                              </span>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            )}

            {throwsBusy && <ListSkeleton rows={5} />}

            {!throwsBusy && throws.length === 0 && (
              <AdminEmpty
                title="Bu süzgeçle atış yok"
                description={throwQuery.username || throwQuery.status
                  ? 'Arama ya da durum süzgecini gevşetmeyi deneyin.'
                  : 'Oyuncular çapayı attıkça satırlar burada görünür.'}
              />
            )}

            {!throwsBusy && throws.length > 0 && (
              <div className="anch-scroll">
                <table className="anch-table">
                  <thead>
                    <tr>
                      <th>Üye</th>
                      <th>Değer</th>
                      <th>Ödül</th>
                      <th>Durum</th>
                      <th>Spin</th>
                      <th>IP</th>
                      <th>Tarih</th>
                      <th className="anch-cell-right">İşlem</th>
                    </tr>
                  </thead>
                  <tbody>
                    {throws.map((row) => {
                      const when = splitDate(row.created_at);
                      const queued = row.status === 'awaiting_provider';
                      return (
                        <tr key={row.id} className={queued ? 'is-queued' : ''}>
                          <td data-label="Üye">
                            {row.user_id ? (
                              <Link to={`/admin/users/${row.user_id}`} className="anch-user">
                                <span className="anch-avatar" aria-hidden="true">
                                  {initials(row.username)}
                                </span>
                                {row.username || '—'}
                              </Link>
                            ) : (
                              <span className="anch-user-none">
                                {row.username || '—'}
                              </span>
                            )}
                          </td>
                          <td data-label="Değer">
                            <span className="anch-amount">{row.prize_value}</span>
                          </td>
                          <td data-label="Ödül">
                            <Pill tone={row.reward_type === 'freebet'
                              ? 'is-info'
                              : (row.reward_type === 'freespin' ? 'is-ok' : 'is-muted')}
                            >
                              {REWARD_LABELS[row.reward_type || ''] || row.reward_type}
                            </Pill>
                          </td>
                          <td data-label="Durum">
                            <Pill tone={
                              row.status === 'awaiting_provider' ? 'is-warn'
                                : row.status === 'completed' ? 'is-ok'
                                  : row.status === 'cancelled' ? 'is-bad' : 'is-muted'
                            }
                            >
                              {STATUS_LABELS[row.status] || row.status}
                            </Pill>
                          </td>
                          <td data-label="Spin">
                            {row.reward_type === 'freespin' ? (
                              <span className="anch-spins">
                                <b>{row.remaining_spins ?? row.spin_count ?? 0}</b>
                                <small>{row.spin_count || 0} hakkın kalanı</small>
                              </span>
                            ) : <span className="anch-dim">—</span>}
                          </td>
                          <td data-label="IP">
                            <span className="anch-mono">{row.ip_address || '—'}</span>
                          </td>
                          <td data-label="Tarih">
                            <span className="anch-when">
                              <b>{when.day}</b>
                              <span>{when.time}</span>
                            </span>
                          </td>
                          <td data-label="İşlem" className="anch-cell-right">
                            {queued && (
                              <button
                                type="button"
                                onClick={() => deliverThrow(row)}
                                disabled={!canWrite}
                                title={canWrite ? '' : 'Bu işlem için "promo.write" yetkisi gerekiyor'}
                                className="anch-mini is-primary"
                              >
                                <CheckCircle2 size={12} aria-hidden="true" /> Şimdi Tanımla
                              </button>
                            )}
                            {row.status === 'completed' && row.delivered_by && (
                              <span className="anch-dim">{row.delivered_by}</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {!throwsBusy && throwsTotal > 0 && (
              <Pager
                total={throwsTotal}
                skip={throwQuery.skip}
                limit={throwQuery.limit}
                count={throws.length}
                onSkip={(skip) => setThrowQuery((current) => ({ ...current, skip }))}
                onLimit={(limit) => {
                  writePageSize('anchor-throws', limit);
                  setThrowQuery((current) => ({ ...current, limit, skip: 0 }));
                }}
              />
            )}
          </section>

          <div className="anch-help">
            <div className="anch-help-card">
              <ShieldCheck size={15} aria-hidden="true" />
              <p>
                <b>Sağlayıcı arızasında ödül yanmaz</b>
                Sağlayıcıya ulaşılamazsa ödül iptal edilmez, kuyrukta kalır —
                oyuncunun kazandığı ödülün gecikmesi yanmasından iyidir.
                <b> Şimdi Tanımla</b> ödülün kaynağına göre doğru yolu kullanır:
                Drakon kampanyasına ekler ya da bağlı oyunda hakkı doğrudan
                açar.
              </p>
            </div>
            <div className="anch-help-card">
              <Timer size={15} aria-hidden="true" />
              <p>
                <b>Teslim modu kuyruğu belirler</b>
                <b> Otomatik tanımlama</b>&apos;da kazanan hemen sağlayıcıya
                yazılır ve kuyruk yalnızca arızada dolar.
                <b> Admin kontrolü</b>&apos;nde her freespin ödülü burada
                bekler. Mod, Ayarlar sekmesinden değiştirilir.
              </p>
            </div>
          </div>
        </>
      )}

      {/* ------------------------------------------------------- freebet - */}
      {view === 'freebets' && (
        <>
          <section className="anch-panel">
            <div className="anch-panel-head">
              <span className="anch-panel-title">
                <Ticket size={15} aria-hidden="true" /> Freebet hakları
              </span>
              <span className="anch-panel-meta">
                {freebetsBusy
                  ? 'Yükleniyor…'
                  : rangeLabel(freebetsTotal, freebetQuery.skip, freebets.length)}
              </span>
            </div>

            <ListToolbar
              search={freebetSearch}
              onSearch={setFreebetSearch}
              filters={FREEBET_STATUS_FILTERS}
              status={freebetQuery.status}
              onStatus={(status) => setFreebetQuery(
                (current) => ({ ...current, status, skip: 0 }))}
            />

            {freebetsBusy && <ListSkeleton rows={5} />}

            {!freebetsBusy && freebets.length === 0 && (
              <AdminEmpty
                title="Bu süzgeçle freebet yok"
                description={freebetQuery.username || freebetQuery.status
                  ? 'Arama ya da durum süzgecini gevşetmeyi deneyin.'
                  : 'Oyuncular ödülünü freebet olarak aldıkça satırlar burada görünür.'}
              />
            )}

            {!freebetsBusy && freebets.length > 0 && (
              <div className="anch-scroll">
                <table className="anch-table">
                  <thead>
                    <tr>
                      <th>Üye</th>
                      <th>Tutar</th>
                      <th>Min. Oran</th>
                      <th>Durum</th>
                      <th>Son Kullanım</th>
                      <th>Verildiği Tarih</th>
                    </tr>
                  </thead>
                  <tbody>
                    {freebets.map((row) => {
                      const when = splitDate(row.created_at);
                      return (
                        <tr key={row.id}>
                          <td data-label="Üye">
                            {row.user_id ? (
                              <Link to={`/admin/users/${row.user_id}`} className="anch-user">
                                <span className="anch-avatar" aria-hidden="true">
                                  {initials(row.username)}
                                </span>
                                {row.username || '—'}
                              </Link>
                            ) : (
                              <span className="anch-user-none">
                                {row.username || '—'}
                              </span>
                            )}
                          </td>
                          <td data-label="Tutar">
                            <span className="anch-amount">{formatMoney(row.amount)}</span>
                          </td>
                          <td data-label="Min. Oran">
                            <span className="anch-mono">
                              {Number(row.min_odd || 1).toFixed(2)}
                            </span>
                          </td>
                          <td data-label="Durum">
                            <Pill tone={
                              row.status === 'active' ? 'is-ok'
                                : row.status === 'expired' ? 'is-bad' : 'is-muted'
                            }
                            >
                              {FREEBET_STATUS_LABELS[row.status] || row.status}
                            </Pill>
                          </td>
                          <td data-label="Son Kullanım">
                            <span className="anch-dim">
                              {row.expires_at ? formatDate(row.expires_at) : 'Süresiz'}
                            </span>
                          </td>
                          <td data-label="Verildiği Tarih">
                            <span className="anch-when">
                              <b>{when.day}</b>
                              <span>{when.time}</span>
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {!freebetsBusy && freebetsTotal > 0 && (
              <Pager
                total={freebetsTotal}
                skip={freebetQuery.skip}
                limit={freebetQuery.limit}
                count={freebets.length}
                onSkip={(skip) => setFreebetQuery((current) => ({ ...current, skip }))}
                onLimit={(limit) => {
                  writePageSize('anchor-freebets', limit);
                  setFreebetQuery((current) => ({ ...current, limit, skip: 0 }));
                }}
              />
            )}
          </section>

          <div className="anch-help">
            <div className="anch-help-card">
              <Ticket size={15} aria-hidden="true" />
              <p>
                <b>Freebet nakit bahisten iki noktada ayrılır</b>
                Kupon açılırken bakiyeden para düşülmez ve kazanan kuponda
                <b> bahis tutarı iade edilmez</b>: ödenen <i>tutar × (oran −
                1)</i>. 20 ₺ / 2.00 oran = 20 ₺, 40 ₺ değil. Bu yüzden freebet
                kuponunda cashout kapalıdır ve tutar çevrim şartına sayılmaz.
              </p>
            </div>
            <div className="anch-help-card">
              <Info size={15} aria-hidden="true" />
              <p>
                <b>Bacakların tamamı iptal olursa hak geri döner</b>
                Kupon void olduğunda para değil <b>hakkın kendisi</b> iade
                edilir ve satır yeniden “Kullanılabilir” olur.
              </p>
            </div>
          </div>
        </>
      )}

      {/* ------------------------------------------------------- ayarlar - */}
      {view === 'settings' && (
        <>
          <section className="anch-panel">
            <div className="anch-panel-head">
              <span className="anch-panel-title">
                <Settings2 size={15} aria-hidden="true" /> Etkinlik ayarları
              </span>
              <span className="anch-panel-meta">
                {dirty ? `${dirtyKeys.length} alan kaydedilmedi` : 'Kayıtlı'}
              </span>
            </div>

            <div className="anch-form">
              <fieldset className="anch-fieldset">
                <div className="anch-fieldset-head">
                  <Anchor size={14} aria-hidden="true" />
                  <span className="anch-fieldset-title">Sayfa görünümü</span>
                  <span className="anch-fieldset-note">Oyuncunun /capa sayfasında gördükleri</span>
                </div>
                <div className="anch-grid">
                  <Field label="Sayfa Başlığı">
                    <input
                      value={settings.anchor_title || ''}
                      onChange={(event) => setSettings({
                        ...settings, anchor_title: event.target.value })}
                      className="admin-input"
                      placeholder="Çapanı At, Ödülünü Kap"
                    />
                  </Field>
                  <Field
                    label="Video"
                    hint="oto — pakete gömülü capa.mp4 kullanılır. İsterseniz doğrudan bir mp4/webm adresi yazın."
                  >
                    <input
                      value={settings.anchor_video_url || ''}
                      onChange={(event) => setSettings({
                        ...settings, anchor_video_url: event.target.value })}
                      className="admin-input"
                      placeholder="oto veya https://.../capa.mp4"
                    />
                  </Field>
                </div>
                <div className="anch-grid is-wide" style={{ marginTop: 12 }}>
                  <Field label="Açıklama">
                    <textarea
                      rows={2}
                      value={settings.anchor_description || ''}
                      onChange={(event) => setSettings({
                        ...settings, anchor_description: event.target.value })}
                      className="admin-input"
                      placeholder="Her gün bir çapa hakkın var."
                    />
                  </Field>
                </div>
              </fieldset>

              <fieldset className="anch-fieldset">
                <div className="anch-fieldset-head">
                  <Users size={14} aria-hidden="true" />
                  <span className="anch-fieldset-title">Katılım kuralları</span>
                  <span className="anch-fieldset-note">Kimin, ne sıklıkla atabileceği</span>
                </div>
                <div className="anch-grid">
                  <Field
                    label="Atış Aralığı (saat)"
                    hint="0 = sınırsız. IP kuralı da bu pencereye bağlıdır."
                  >
                    <input
                      type="number" min="0"
                      value={settings.anchor_cooldown_hours ?? 24}
                      onChange={(event) => setSettings({
                        ...settings, anchor_cooldown_hours: event.target.value })}
                      className="admin-input"
                    />
                  </Field>
                  <Field
                    label="Minimum Toplam Yatırım"
                    hint="0 = şart yok. Üyenin ömür boyu onaylı yatırım toplamına bakılır."
                  >
                    <input
                      type="number" min="0" step="0.01"
                      value={settings.anchor_min_deposit ?? 0}
                      onChange={(event) => setSettings({
                        ...settings, anchor_min_deposit: event.target.value })}
                      className="admin-input"
                    />
                  </Field>
                </div>
                <div className="anch-switches" style={{ marginTop: 12 }}>
                  <label className={`admin-switch${settings.anchor_require_telegram ? ' on' : ''}`}>
                    <input
                      type="checkbox"
                      checked={Boolean(settings.anchor_require_telegram)}
                      onChange={(event) => setSettings({
                        ...settings, anchor_require_telegram: event.target.checked })}
                    />
                    <span className="admin-switch-track" aria-hidden="true" />
                    <span className="min-w-0">
                      <span className="anch-switch-title">
                        <Send size={13} aria-hidden="true" /> Telegram üyeliği zorunlu
                      </span>
                      <span className="anch-switch-text">
                        Oyuncu hesabını bota bağlamalı ve topluluk grubunda
                        olmalıdır. Grup kimliği Ayarlar → Telegram altında
                        tanımlanır; eksikse bu kutu kaydedilmez.
                      </span>
                    </span>
                  </label>
                  <label className={`admin-switch${settings.anchor_single_ip ? ' on' : ''}`}>
                    <input
                      type="checkbox"
                      checked={Boolean(settings.anchor_single_ip)}
                      onChange={(event) => setSettings({
                        ...settings, anchor_single_ip: event.target.checked })}
                    />
                    <span className="admin-switch-track" aria-hidden="true" />
                    <span className="min-w-0">
                      <span className="anch-switch-title">
                        <ShieldCheck size={13} aria-hidden="true" /> IP başına tek atış
                      </span>
                      <span className="anch-switch-text">
                        Aynı bağlantıdan aynı pencerede yalnızca bir hesap çapa
                        atabilir. Atış aralığı 0 iken pencere olmadığı için bu
                        kural da uygulanmaz.
                      </span>
                    </span>
                  </label>
                </div>
              </fieldset>

              <fieldset className="anch-fieldset">
                <div className="anch-fieldset-head">
                  <Zap size={14} aria-hidden="true" />
                  <span className="anch-fieldset-title">Freespin teslimi</span>
                  <span className="anch-fieldset-note">Anahtar anında kaydedilir</span>
                </div>
                <div className="anch-mode">
                  <div className="anch-mode-options" role="group" aria-label="Freespin teslim modu">
                    <button
                      type="button"
                      onClick={() => !autoDelivery && saveSettings({
                        anchor_freespin_auto_delivery: true })}
                      disabled={savingSettings || !canWrite}
                      className={`anch-mode-btn${autoDelivery ? ' is-active' : ''}`}
                      aria-pressed={autoDelivery}
                    >
                      <Zap size={12} aria-hidden="true" /> Otomatik tanımlama
                    </button>
                    <button
                      type="button"
                      onClick={() => autoDelivery && saveSettings({
                        anchor_freespin_auto_delivery: false })}
                      disabled={savingSettings || !canWrite}
                      className={`anch-mode-btn${autoDelivery ? '' : ' is-active'}`}
                      aria-pressed={!autoDelivery}
                    >
                      <ListChecks size={12} aria-hidden="true" /> Admin kontrolü
                    </button>
                  </div>
                  <p className="anch-mode-note">
                    {autoDelivery
                      ? 'Kazanan hemen sağlayıcıda tanımlanır: Drakon '
                        + 'kampanyasına eklenir ya da bağlı oyunda hak açılır. '
                        + 'Kuyruk yalnızca sağlayıcı arızasında dolar.'
                      : 'Her freespin ödülü Atışlar kuyruğunda bekler ve '
                        + '“Şimdi Tanımla” ile siz açarsınız. Oyuncu ödülünü '
                        + 'o ana kadar kullanamaz.'}
                  </p>
                </div>
              </fieldset>

              <fieldset className="anch-fieldset">
                <div className="anch-fieldset-head">
                  <Ticket size={14} aria-hidden="true" />
                  <span className="anch-fieldset-title">Freebet kuralları</span>
                  <span className="anch-fieldset-note">Ödül freebet olarak seçildiğinde</span>
                </div>
                <div className="anch-grid">
                  <Field
                    label="Freebet Minimum Oranı"
                    hint="Kuponun taşıması gereken en düşük toplam oran."
                  >
                    <input
                      type="number" min="1" step="0.01"
                      value={settings.anchor_freebet_min_odd ?? 1.5}
                      onChange={(event) => setSettings({
                        ...settings, anchor_freebet_min_odd: event.target.value })}
                      className="admin-input"
                    />
                  </Field>
                  <Field
                    label="Freebet Geçerlilik (gün)"
                    hint="0 = süresiz."
                  >
                    <input
                      type="number" min="0"
                      value={settings.anchor_freebet_expiry_days ?? 7}
                      onChange={(event) => setSettings({
                        ...settings, anchor_freebet_expiry_days: event.target.value })}
                      className="admin-input"
                    />
                  </Field>
                </div>
              </fieldset>

              <div className={`anch-savebar${dirty ? ' is-dirty' : ''}`}>
                <span className="anch-savebar-note">
                  {dirty
                    ? `${dirtyKeys.length} alan kaydedilmedi`
                    : 'Bütün alanlar kayıtlı'}
                </span>
                <div className="anch-savebar-actions">
                  {dirty && (
                    <button
                      type="button"
                      onClick={() => setSettings(savedSettings)}
                      disabled={savingSettings}
                      className="admin-btn-secondary"
                    >
                      <X size={14} aria-hidden="true" /> Değişiklikleri geri al
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => saveSettings()}
                    disabled={savingSettings || !canWrite || !dirty}
                    title={canWrite ? '' : 'Bu işlem için "promo.write" yetkisi gerekiyor'}
                    className="admin-btn-primary"
                  >
                    {savingSettings
                      ? <Loader2 size={14} className="animate-spin" aria-hidden="true" />
                      : <Save size={14} aria-hidden="true" />}
                    Ayarları Kaydet
                  </button>
                </div>
              </div>
            </div>
          </section>

          <div className="anch-help">
            <div className="anch-help-card">
              <Send size={15} aria-hidden="true" />
              <p>
                <b>Telegram şartı yarım kurulumla açılmaz</b>
                Bot token, topluluk grubu kimliği, davet bağlantısı ve webhook
                secret <b>Ayarlar → Telegram</b> altında tanımlanır. Eksikse
                kayıt 400 ile geri döner — yarım bir kurulumun bütün oyuncuları
                ödülden kilitlemesi engellenir. Topluluk grubu, bildirim
                grubundan farklıdır.
              </p>
            </div>
            <div className="anch-help-card">
              <Link2 size={15} aria-hidden="true" />
              <p>
                <b>Kampanya kartından da açılabilir</b>
                Bonus Kampanyaları penceresindeki <b>Kampanya sayfasına
                yönlendir</b> seçeneği <code>/capa</code> adresini hazır
                doldurur; kartın düğmesi oyuncuyu buraya getirir.
              </p>
            </div>
          </div>
        </>
      )}

      {/* ------------------------------------------------------ pencereler */}
      {modal && (
        <div className="anch-modal-layer" role="presentation">
                    <button
            type="button"
            className="anch-modal-backdrop"
            aria-label="Pencereyi kapat"
            onClick={() => !busy && closeModals()}
          />
          <div
            className="admin-card admin-modal anch-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Ödül düzenleme penceresi"
            style={{ maxWidth: 460 }}
          >
            <div className="admin-modal-head">
              <div className="admin-modal-icon"><Gift size={19} /></div>
              <div className="min-w-0">
                <h2 className="admin-card-title">
                  {modal === 'create' ? 'Yeni Ödül' : 'Ödülü Düzenle'}
                </h2>
                <p className="admin-card-hint">
                  Değer hem freespin adedini hem freebet tutarını belirler.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setModal(null)}
                className="icon-btn view ml-auto"
                aria-label="Pencereyi kapat"
              >
                <X size={15} />
              </button>
            </div>

            <div className="admin-modal-body">
              <div className="anch-modal-body">
                <div className="anch-preview">
                  <span
                    className="anch-preview-dot"
                    style={{ background: form.color }}
                    aria-hidden="true"
                  >
                    {form.value || 0}
                  </span>
                  <span className="anch-preview-text">
                    <b>{form.value || 0} freespin veya {form.value || 0} ₺ freebet</b>
                    Oyuncu çapayı attığında bu dilim bu renkle görünür.
                  </span>
                </div>

                <div className="anch-grid">
                  <Field
                    label="Değer"
                    hint={isPrizeCampaignReady(form)
                      ? 'Adet kampanyaya gömülüdür; değiştirmek için önce kampanyayı iptal edin.'
                      : 'Freespin adedi ve freebet tutarı (₺) bu sayıdır.'}
                  >
                    <input
                      type="number" min="1"
                      disabled={isPrizeCampaignReady(form)}
                      value={form.value}
                      onChange={(event) => setForm({
                        ...form, value: event.target.value })}
                      className="admin-input"
                    />
                  </Field>
                  <Field
                    label="Ağırlık"
                    hint="Olasılık ağırlığın toplam ağırlığa oranıdır. 0 = hiç çıkmaz."
                  >
                    <input
                      type="number" min="0"
                      value={form.weight}
                      onChange={(event) => setForm({
                        ...form, weight: event.target.value })}
                      className="admin-input"
                    />
                  </Field>
                  <Field label="Sıra" hint="Küçük değer önce çizilir.">
                    <input
                      type="number"
                      value={form.order}
                      onChange={(event) => setForm({
                        ...form, order: event.target.value })}
                      className="admin-input"
                    />
                  </Field>
                </div>

                <div className="anch-field">
                  <span>Renk</span>
                  <div className="anch-colors">
                    {PALETTE.map((color) => (
                      <button
                        key={color}
                        type="button"
                        onClick={() => setForm({ ...form, color })}
                        className={`anch-color${form.color === color ? ' is-active' : ''}`}
                        style={{ background: color }}
                        aria-label={color}
                        aria-pressed={form.color === color}
                      />
                    ))}
                  </div>
                </div>

                <label className={`admin-switch${form.active ? ' on' : ''}`}>
                  <input
                    type="checkbox"
                    checked={Boolean(form.active)}
                    disabled={!isPrizeFreespinReady(form)}
                    onChange={(event) => setForm({
                      ...form, active: event.target.checked })}
                  />
                  <span className="admin-switch-track" aria-hidden="true" />
                  <span className="min-w-0">
                    <span className="anch-switch-title">
                      <CheckCircle2 size={13} aria-hidden="true" /> Çekilişte aktif
                    </span>
                    <span className="anch-switch-text">
                      {isPrizeFreespinReady(form)
                        ? 'Pasif ödül çekilişe girmez ve oyuncuya hiç çıkmaz.'
                        : 'Önce freespin kaynağını bağlayın: Drakon kampanyası '
                          + 'ya da bir oyun. Kampanya oluşturulduğunda ödül '
                          + 'kendiliğinden aktifleşir.'}
                    </span>
                  </span>
                </label>
              </div>
            </div>

            <div className="admin-modal-foot">
              <span className="admin-modal-summary">
                {isPrizeFreespinReady(form)
                  ? 'Kaynak bağlı'
                  : 'Ödül önce pasif kaydedilir'}
              </span>
              <div className="ml-auto flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setModal(null)}
                  className="admin-btn-secondary"
                >
                  Vazgeç
                </button>
                <button
                  type="button"
                  onClick={savePrize}
                  disabled={busy}
                  className="admin-btn-primary"
                >
                  {busy
                    ? <Loader2 size={14} className="animate-spin" aria-hidden="true" />
                    : <Save size={14} aria-hidden="true" />}
                  Kaydet
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {campaignPrize && (
        <div className="anch-modal-layer" role="presentation">
                    <button
            type="button"
            className="anch-modal-backdrop"
            aria-label="Pencereyi kapat"
            onClick={() => !busy && closeModals()}
          />
          <div
            className="admin-card admin-modal anch-modal campaign-modal-card"
            role="dialog"
            aria-modal="true"
            aria-label="Drakon kampanyası penceresi"
            style={{ maxWidth: 560 }}
          >
            <div className="admin-modal-head">
              <div className="admin-modal-icon"><Zap size={19} /></div>
              <div className="min-w-0">
                <h2 className="admin-card-title">
                  {campaignPrize.value} Free Spin Kampanyası
                </h2>
                <p className="admin-card-hint">
                  Adet kampanyaya gömülüdür ve ödül değerinden alınır.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setCampaignPrize(null)}
                className="icon-btn view ml-auto"
                aria-label="Pencereyi kapat"
              >
                <X size={15} />
              </button>
            </div>

            <div className="admin-modal-body">
              <p className="anch-modal-hint">
                Sağlayıcı <b>geçmiş başlangıç zamanını kabul etmiyor</b>:
                kampanya birkaç dakika sonra başlar. Bahis tutarı serbest
                yazılmaz, sağlayıcının izin verdiği listeden seçilir.
              </p>
              <div className="anch-modal-body">
                <CampaignCombobox
                  label="Sağlayıcı"
                  items={filteredVendors}
                  value={campaignForm.vendor}
                  search={vendorSearch}
                  onSearchChange={searchVendors}
                  onSelect={(vendor) => selectVendor(vendor)}
                  onClear={() => selectVendor('')}
                  getValue={(vendor) => vendor}
                  getLabel={(vendor) => vendor}
                  placeholder="Sağlayıcı ara veya seç..."
                  emptyMessage={vendorSearch
                    ? `“${vendorSearch}” için sağlayıcı bulunamadı.`
                    : 'Drakon kampanya açılabilecek sağlayıcı bildirmedi; '
                      + 'Oyun Kontrol → Oyun Servisleri → Drakon → Ayarlar '
                      + 'bilgilerini ve agent yetkisini doğrulayın.'}
                  loading={vendorsBusy}
                />

                <CampaignCombobox
                  label="Oyun"
                  items={filteredLimits}
                  value={campaignForm.game_id}
                  search={gameSearch}
                  onSearchChange={searchGames}
                  onSelect={selectGame}
                  onClear={() => selectGame(null)}
                  getValue={(game) => String(game.game_id)}
                  getLabel={(game) => (game.game_name
                    ? `${game.game_name} (${game.game_id})`
                    : `Oyun ${game.game_id}`)}
                  placeholder={limitsBusy
                    ? 'Oyunlar yükleniyor…'
                    : campaignForm.vendor
                      ? 'Oyun adı veya kodu ara...'
                      : 'Önce sağlayıcı seçin'}
                  emptyMessage={gameSearch
                    ? `“${gameSearch}” için oyun bulunamadı.`
                    : hiddenGamesNotice
                      || 'Bu sağlayıcı için kampanya açılabilecek oyun yok.'}
                  loading={limitsBusy}
                  disabled={!campaignForm.vendor || limitsBusy}
                />

                {hiddenGamesNotice && (
                  <div className="anch-note is-warn">
                    <AlertTriangle size={14} aria-hidden="true" />
                    <div className="anch-note-body">{hiddenGamesNotice}</div>
                  </div>
                )}

                <div className="anch-grid">
                  <Field
                    label="Spin Başına Bahis Tutarı"
                    hint={selectedLimit
                      ? `Her ücretsiz çevirmede uygulanır • Para birimi: ${selectedLimit.currency_code}`
                      : 'Sağlayıcının izin verdiği değerlerden seçilir.'}
                  >
                    <select
                      value={campaignForm.total_bet}
                      onChange={(event) => setCampaignForm({
                        ...campaignForm, total_bet: event.target.value })}
                      className="admin-input"
                      disabled={!selectedLimit}
                    >
                      <option value="">
                        {selectedLimit ? 'Seçin' : 'Önce oyun seçin'}
                      </option>
                      {(selectedLimit?.limits || []).map((value) => (
                        <option key={String(value)} value={String(value)}>
                          {value}
                        </option>
                      ))}
                    </select>
                  </Field>

                  <Field
                    label="Kampanya Süresi (gün)"
                    hint="Süre dolduğunda ödüle yeni bir kampanya açmanız gerekir."
                  >
                    <input
                      type="number" min="1" max="365"
                      value={campaignForm.duration_days}
                      onChange={(event) => setCampaignForm({
                        ...campaignForm,
                        duration_days: Number(event.target.value) || 1,
                      })}
                      className="admin-input"
                    />
                  </Field>
                </div>
              </div>
            </div>

            <div className="admin-modal-foot">
              <span className="admin-modal-summary">
                {campaignBlocker || `${campaignPrize.value} spin • hazır`}
              </span>
              <div className="ml-auto flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setCampaignPrize(null)}
                  className="admin-btn-secondary"
                >
                  Vazgeç
                </button>
                <button
                  type="button"
                  onClick={createCampaign}
                  disabled={busy || Boolean(campaignBlocker)}
                  title={campaignBlocker || 'Kampanyayı sağlayıcıda aç'}
                  className="admin-btn-primary"
                >
                  {busy
                    ? <Loader2 size={14} className="animate-spin" aria-hidden="true" />
                    : <Zap size={14} aria-hidden="true" />}
                  Kampanyayı Oluştur
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {gamePrize && (
        <div className="anch-modal-layer" role="presentation">
                    <button
            type="button"
            className="anch-modal-backdrop"
            aria-label="Pencereyi kapat"
            onClick={() => !busy && closeModals()}
          />
          <div
            className="admin-card admin-modal anch-modal campaign-modal-card"
            role="dialog"
            aria-modal="true"
            aria-label="Freespin oyunu seçme penceresi"
            style={{ maxWidth: 560 }}
          >
            <div className="admin-modal-head">
              <div className="admin-modal-icon"><Gamepad2 size={19} /></div>
              <div className="min-w-0">
                <h2 className="admin-card-title">
                  {gamePrize.value} Freespin — Oyun Seç
                </h2>
                <p className="admin-card-hint">
                  Kampanya açılmaz; hak ödül anında bu oyunda tanımlanır.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setGamePrize(null)}
                className="icon-btn view ml-auto"
                aria-label="Pencereyi kapat"
              >
                <X size={15} />
              </button>
            </div>

            <div className="admin-modal-body">
              <p className="anch-modal-hint">
                Listede yalnızca <b>oyuncu bazlı freespin verebilen</b>
                sağlayıcıların sitede aktif oyunları vardır — Drakon burada
                çıkmaz, onun yolu kampanyadır. Katalog binlerce satır olduğu
                için arama sunucuda yapılır.
              </p>
              <div className="anch-modal-body">
                <div className="anch-field">
                  <span>Oyun Ara</span>
                  <div className="anch-search">
                    <Search size={15} aria-hidden="true" />
                    <input
                      type="text"
                      value={gameQuery}
                      onChange={(event) => setGameQuery(event.target.value)}
                      placeholder="Oyun ya da üretici adı..."
                      className="admin-input pl-10 pr-10"
                    />
                    {gameQuery && (
                      <button
                        type="button"
                        className="anch-search-clear"
                        onClick={() => setGameQuery('')}
                        aria-label="Aramayı temizle"
                      >
                        <X size={13} />
                      </button>
                    )}
                  </div>
                </div>

                <div className="anch-picker">
                  {gameOptionsBusy && (
                    <div className="anch-picker-state">
                      <Loader2 size={14} className="animate-spin" /> Oyunlar yükleniyor…
                    </div>
                  )}
                  {!gameOptionsBusy && gameOptions.length === 0 && (
                    <div className="anch-picker-state">
                      {gameQuery
                        ? `“${gameQuery}” için oyun bulunamadı.`
                        : 'Freespin verebilen aktif oyun bulunamadı.'}
                    </div>
                  )}
                  {!gameOptionsBusy && gameOptions.map((game) => {
                    const selected = String(gameForm.game_id) === String(game.id);
                    return (
                      <button
                        key={game.id}
                        type="button"
                        onClick={() => setGameForm({
                          ...gameForm, game_id: String(game.id) })}
                        className={`anch-picker-row${selected ? ' is-active' : ''}`}
                        aria-pressed={selected}
                      >
                        <span className="anch-picker-text">
                          <b>{game.name}</b>
                          <span>
                            {game.api_provider_name}
                            {game.provider ? ` • ${game.provider}` : ''}
                          </span>
                        </span>
                        {selected && (
                          <CheckCircle2 size={15} className="anch-picker-check" />
                        )}
                      </button>
                    );
                  })}
                </div>
                {gameOptionsTotal > gameOptions.length && (
                  <p className="anch-hint">
                    {gameOptionsTotal} oyundan ilk {gameOptions.length} tanesi
                    gösteriliyor; aramayı daraltın.
                  </p>
                )}

                <div className="anch-grid">
                  <Field
                    label="Spin Başına Bahis"
                    hint="Boş bırakılırsa Freespin Kontrol sayfasındaki varsayılan bahis kullanılır."
                  >
                    <input
                      type="number" min="0" step="0.01"
                      value={gameForm.bet}
                      onChange={(event) => setGameForm({
                        ...gameForm, bet: event.target.value })}
                      placeholder="Varsayılan"
                      className="admin-input"
                    />
                  </Field>
                </div>
              </div>
            </div>

            <div className="admin-modal-foot">
              <span className="admin-modal-summary">
                {gameForm.game_id
                  ? `${gamePrize.value} spin bu oyunda tanımlanacak`
                  : 'Önce listeden bir oyun seçin'}
              </span>
              <div className="ml-auto flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setGamePrize(null)}
                  className="admin-btn-secondary"
                >
                  Vazgeç
                </button>
                <button
                  type="button"
                  onClick={saveFreespinGame}
                  disabled={busy || !gameForm.game_id}
                  className="admin-btn-primary"
                >
                  {busy
                    ? <Loader2 size={14} className="animate-spin" aria-hidden="true" />
                    : <Sparkles size={14} aria-hidden="true" />}
                  Oyunu Bağla
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={Boolean(confirmState)}
        title={confirmState?.title || ''}
        description={confirmState?.description || ''}
        confirmLabel={confirmState?.confirmLabel || 'Onayla'}
        tone={confirmState?.tone || 'danger'}
        busy={confirmBusy}
        onCancel={() => !confirmBusy && setConfirmState(null)}
        onConfirm={runConfirm}
      />
    </div>
  );
};

export default AnchorSettings;
